import type { ComponentProps } from 'react';

import { clsxm } from '@vs-shop/utils';

import { ChevronRight } from 'lucide-react';

import { renderThrough, type RenderElement } from '../../util/render/render.tsx';

/**
 * The trail back up from wherever a shopper is standing:
 *
 *   <Breadcrumb aria-label={label}>
 *     <BreadcrumbList>
 *       <BreadcrumbItem>
 *         <BreadcrumbLink render={<Link href={home} />}><Home /></BreadcrumbLink>
 *       </BreadcrumbItem>
 *       <BreadcrumbSeparator />
 *       <BreadcrumbItem>
 *         <BreadcrumbPage>Buggy 1:8</BreadcrumbPage>
 *       </BreadcrumbItem>
 *     </BreadcrumbList>
 *   </Breadcrumb>
 *
 * Both storefronts on this platform show one, in the same anatomy: a home mark,
 * a run of links separated by a mark, and the page the shopper is on, unlinked.
 * What differs between them is the copy, the router the links go through and
 * the glyph in the separator — all of which are the composition's.
 *
 * The list scrolls sideways rather than wrapping: a deep taxon trail on a phone
 * is longer than the viewport, and a trail that wrapped would push the page's
 * own opening down by a line or two depending on how deep the shopper happens
 * to be.
 *
 * **No part here renders a heading, and the current page is not a link.**
 * `BreadcrumbPage` carries `aria-current="page"` on a neutral element: a trail
 * is not a document outline, and the page a shopper is already on is not
 * somewhere they can go.
 *
 * The trail's own accessible name rides the spread native `aria-label` — the
 * word is copy, and copy is the tenant's.
 */

export type BreadcrumbProps = ComponentProps<'nav'>;

/**
 * The trail's landmark.
 *
 * Paints nothing, so it declares no recipe — it exists to give the run of links
 * a name assistive technology can announce, which the composition supplies.
 */
export const Breadcrumb = (props: BreadcrumbProps) => <nav data-slot="breadcrumb" {...props} />;

/**
 * The run itself: one line, scrolled rather than wrapped, with its scrollbar
 * hidden because a trail is chrome and a visible bar under it reads as content.
 */
const breadcrumbListClasses =
  'scrollbar-hidden text-foreground-muted tab:gap-2.5 flex items-center gap-1.5 overflow-x-scroll text-sm tracking-tight';

export type BreadcrumbListProps = ComponentProps<'ol'>;

/** The ordered list of steps. */
export const BreadcrumbList = ({ className, ...props }: BreadcrumbListProps) => (
  <ol data-slot="breadcrumb-list" className={clsxm(breadcrumbListClasses, className)} {...props} />
);

/** One step, and whatever sits inside it — never broken across lines. */
const breadcrumbItemClasses = 'inline-flex items-center gap-1.5 whitespace-nowrap';

export type BreadcrumbItemProps = ComponentProps<'li'>;

/** A step of the trail. */
export const BreadcrumbItem = ({ className, ...props }: BreadcrumbItemProps) => (
  <li data-slot="breadcrumb-item" className={clsxm(breadcrumbItemClasses, className)} {...props} />
);

/** A step that goes somewhere: underlined only once it is pointed at. */
const breadcrumbLinkClasses = 'hover:underline hover:underline-offset-2';

export type BreadcrumbLinkProps = ComponentProps<'a'> & {
  /**
   * The element to paint instead of the neutral `<a>` — a router's link, which
   * only the tenant has. Required, and `undefined` when a plain anchor is what
   * the composition wants.
   */
  readonly render?: RenderElement;
};

/**
 * A linked step of the trail.
 *
 * Its neutral element is an `<a>` rather than the `render` helper's `<span>`:
 * a step that goes somewhere is a link whether or not a router is involved, and
 * a composition handing in `render={<Link/>}` is replacing the anchor rather
 * than promoting a span into one.
 */
export const BreadcrumbLink = ({ className, render, ...props }: BreadcrumbLinkProps) => {
  const classes = clsxm(breadcrumbLinkClasses, className);

  return (
    renderThrough(render, 'breadcrumb-link', classes, props) ?? (
      <a data-slot="breadcrumb-link" className={classes} {...props} />
    )
  );
};

/**
 * The page the shopper is standing on.
 *
 * `role="link"` with `aria-disabled` rather than an anchor: it reads as part of
 * the same run to assistive technology without offering a destination that is
 * where the shopper already is.
 */
const breadcrumbPageClasses = 'text-foreground-muted font-normal whitespace-nowrap';

export type BreadcrumbPageProps = ComponentProps<'span'>;

/** The current page's step. */
export const BreadcrumbPage = ({ className, ...props }: BreadcrumbPageProps) => (
  <span
    data-slot="breadcrumb-page"
    role="link"
    aria-disabled="true"
    aria-current="page"
    className={clsxm(breadcrumbPageClasses, className)}
    {...props}
  />
);

/**
 * The mark between two steps.
 *
 * Ornament, so it is hidden from assistive technology and carries no tab stop:
 * the trail's structure is the list, and a shopper listening to it does not
 * need a chevron read out between every item. A composition with a glyph of its
 * own passes it as children.
 */
const breadcrumbSeparatorClasses = 'text-foreground/20 [&>svg]:size-4';

export type BreadcrumbSeparatorProps = ComponentProps<'li'>;

/** The step divider. */
export const BreadcrumbSeparator = ({
  children,
  className,
  ...props
}: BreadcrumbSeparatorProps) => (
  <li
    data-slot="breadcrumb-separator"
    role="presentation"
    aria-hidden="true"
    className={clsxm(breadcrumbSeparatorClasses, className)}
    {...props}
  >
    {children ?? <ChevronRight strokeWidth={2} />}
  </li>
);
